/* eslint-disable no-undef */
<template>
  <div class="inventory-toolbar-root">
    <!-- Div strip keeps search row in normal document flow so fields don't overlap on narrow viewports -->
    <div
      class="bg-red-darken-3 inventory-toolbar-strip pt-2 pb-2 pb-md-3 px-3 px-md-6 px-lg-8"
    >
      <v-row
        align="center"
        class="inventory-toolbar-search-row flex-wrap"
        no-gutters
      >
        <!-- md+: title | divider | list icon — hidden for dual-field search (more room) -->
        <v-col
          v-if="!isComboSearchType"
          cols="auto"
          class="d-none d-md-flex align-center flex-shrink-0 inventory-toolbar-brand ga-2"
        >
          <div class="text-subtitle-1 text-white text-truncate">
            {{ $t("parts.inventory.label") }}
          </div>
          <template
            v-if="searchType === 'title' || searchType === 'multipleTitles'"
          >
            <v-divider class="flex-shrink-0" inset vertical />
            <v-btn
              variant="text"
              class="flex-shrink-0"
              @click="openSearchList"
            >
              <v-icon>mdi-format-list-checkbox</v-icon>
            </v-btn>
          </template>
        </v-col>

        <v-spacer class="d-none" />

        <!-- Search + type + advanced: right cluster; sm–: icon stays beside field -->
        <v-col cols="12" class="inventory-toolbar-right min-width-0">
          <div
            class="d-flex flex-wrap align-center ga-2 ga-md-3 w-100 justify-start"
            :class="[
              { 'inventory-toolbar-search-combo': isComboSearchType },
              isComboSearchType ? 'justify-md-start' : 'justify-md-end',
            ]"
          >
            <!-- Row 1 on mobile: icon + search field -->
            <div
              class="d-flex align-center ga-2 flex-grow-1 min-width-0 inventory-toolbar-fields-row"
            >
              <v-btn
                v-if="searchType === 'title' || searchType === 'multipleTitles'"
                variant="text"
                class="d-md-none flex-shrink-0"
                @click="openSearchList"
              >
                <v-icon>mdi-format-list-checkbox</v-icon>
              </v-btn>

            <div class="inventory-toolbar-fields flex-grow-1 min-width-0">
              <!-- SEARCH BY TITLE -->
              <v-autocomplete
                v-if="searchType === 'title'"
                v-model="titleCode"
                v-model:search="searchTitle"
                v-model:menu="menuOpen"
                :loading="loadingTitle"
                :items="titles"
                :label="$t('parts.inventory.search')"
                item-value="partCode"
                :item-title="() => ''"
                :custom-filter="customFilter"
                variant="solo"
                color="grey-darken-4"
                append-inner-icon="mdi-magnify"
                single-line
                hide-details
                hide-no-data
                clearable
                autocomplete="off"
                class="inventory-field-on-red"
                @keyup.enter="searchMethod"
                @click:append-inner="searchMethod"
                @update:search="updateSearch"
              >
                <template #item="{ item, props }">
                  <v-list-item
                    v-bind="props"
                    class="listRowColor"
                    @click="selectCode(item)"
                  >
                    <!-- Main Title -->
                    <div class="text-start pa-2">
                      <span class="caption">Info Car - </span>
                      <span
                        class="font-weight-bold"
                        v-html="
                          highlight(
                            item?.raw?.title?.[$store.getters.language] ||
                              'No Title',
                            searchTitle,
                          )
                        "
                      />
                    </div>

                    <!-- Related Titles (Limit to a few to avoid excessive rendering) -->
                    <div
                      v-if="item?.raw?.related?.length"
                      class="related-scroll hidden-sm-and-down pa-2"
                    >
                      <span
                        v-for="(el, idx) in item.raw.related"
                        :key="idx"
                        class="mr-2"
                        style="font-size: 10px"
                        v-html="
                          highlight(
                            el?.title?.[$store.getters.language] ||
                              'No Related Title',
                            searchTitle,
                          )
                        "
                      />
                    </div>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <span v-if="item">
                    {{ item?.raw?.title?.[$store.getters.language] }}
                  </span>
                </template>
              </v-autocomplete>

              <!--SEARCH BY MULTIPLE TITLES-->
              <v-autocomplete
                v-else-if="searchType === 'multipleTitles'"
                v-model="selectedGroup"
                v-model:search="searchMultipleTitles"
                v-model:menu="menuMultipleOpen"
                :loading="loadingMultipleTitles"
                :items="titleGroups"
                :label="$t('parts.movements.search')"
                :item-title="`title.${$store.getters.language}`"
                item-value="_id"
                variant="solo"
                color="grey-darken-4"
                append-inner-icon="mdi-magnify"
                single-line
                hide-details
                hide-no-data
                return-object
                class="inventory-field-on-red"
                @update:model-value="onGroupSelected"
                @keyup.enter="searchMethod()"
                @click:append-inner="searchMethod()"
              />

              <!--SEARCH BY MULTIPLE TITLES AND NOTES-->
              <div
                v-else-if="searchType === 'multipleTitlesNote'"
                class="d-flex flex-column flex-lg-row ga-2 w-100 inventory-toolbar-double-field"
              >
                <v-autocomplete
                  v-model="selectedGroup"
                  v-model:search="searchMultipleTitles"
                  v-model:menu="menuMultipleOpen"
                  :loading="loadingMultipleTitles"
                  :items="titleGroups"
                  :label="$t('parts.movements.search')"
                  :item-title="`title.${$store.getters.language}`"
                  item-value="_id"
                  variant="solo"
                  color="grey-darken-4"
                  class="inventory-field-on-red inventory-toolbar-note-field flex-grow-1"
                  single-line
                  hide-details
                  hide-no-data
                  return-object
                  @update:model-value="onGroupNoteSelected"
                />
                <v-text-field
                  v-model.trim="note"
                  :label="$t('parts.inventory.form.notes')"
                  variant="solo"
                  color="grey-darken-4"
                  append-inner-icon="mdi-magnify"
                  single-line
                  hide-details
                  class="inventory-field-on-red inventory-toolbar-note-field mr-1 flex-grow-1"
                  @keyup.enter="searchMethod()"
                  @click:append-inner="searchMethod()"
                />
              </div>
              <!--SEARCH BY MULTIPLE TITLES AND GENERAL SEARCH-->
              <div
                v-else-if="searchType === 'multipleTitlesOther'"
                class="d-flex flex-column flex-lg-row ga-2 w-100 inventory-toolbar-double-field"
              >
                <v-autocomplete
                  v-model="selectedGroup"
                  v-model:search="searchMultipleTitles"
                  v-model:menu="menuMultipleOpen"
                  :loading="loadingMultipleTitles"
                  :items="titleGroups"
                  :placeholder="$t('parts.inventory.multiple_titles')"
                  :item-title="`title.${$store.getters.language}`"
                  item-value="_id"
                  variant="solo"
                  color="grey-darken-4"
                  class="inventory-field-on-red inventory-toolbar-note-field inventory-toolbar-combo-group flex-grow-1"
                  single-line
                  hide-details
                  hide-no-data
                  return-object
                  @update:model-value="onGroupOtherSelected"
                />
                <v-text-field
                  v-model.trim="groupGeneralSearch"
                  :label="$t('parts.inventory.other')"
                  variant="solo"
                  color="grey-darken-4"
                  append-inner-icon="mdi-magnify"
                  single-line
                  hide-details
                  class="inventory-field-on-red mr-1 inventory-toolbar-note-field inventory-toolbar-combo-term flex-grow-1"
                  @keyup.enter="searchMethod()"
                  @click:append-inner="searchMethod()"
                />
              </div>
              <!--SEARCH BY  TITLE AND CODE-->
              <div
                v-else-if="searchType === 'titleAndCode'"
                class="d-flex flex-column flex-lg-row ga-2 w-100 inventory-toolbar-double-field"
              >
                <v-autocomplete
                  v-model="titleCode"
                  v-model:search="searchTitle"
                  v-model:menu="menuOpen"
                  :loading="loadingTitle"
                  :items="titles"
                  :label="$t('parts.inventory.title')"
                  item-value="partCode"
                  :item-title="() => ''"
                  :custom-filter="customFilter"
                  flat
                  variant="solo"
                  color="grey-darken-4"
                  single-line
                  hide-details
                  hide-no-data
                  clearable
                  autocomplete="off"
                  class="inventory-field-on-red inventory-toolbar-note-field flex-grow-1 mr-lg-2"
                  @update:search="updateSearch"
                >
                  <template #item="{ item, props }">
                    <v-list-item
                      v-bind="props"
                      class="listRowColor"
                      @click="selectCode(item)"
                    >
                      <div class="text-start pa-2">
                        <span class="caption">Info Car - </span>
                        <span
                          class="font-weight-bold"
                          v-html="
                            highlight(
                              item?.raw?.title?.[$store.getters.language] ||
                                'No Title',
                              searchTitle,
                            )
                          "
                        />
                      </div>
                      <div
                        v-if="item?.raw?.related?.length"
                        class="related-scroll hidden-sm-and-down pa-2"
                      >
                        <span
                          v-for="(el, idx) in item.raw.related"
                          :key="idx"
                          class="mr-2"
                          style="font-size: 10px"
                          v-html="
                            highlight(
                              el?.title?.[$store.getters.language] ||
                                'No Related Title',
                              searchTitle,
                            )
                          "
                        />
                      </div>
                    </v-list-item>
                  </template>
                  <template #selection="{ item }">
                    <span v-if="item">
                      {{ item?.raw?.title?.[$store.getters.language] }}
                    </span>
                  </template>
                </v-autocomplete>
                <v-text-field
                  v-model.trim="code"
                  :label="$t('parts.table.manufacturer_code')"
                  flat
                  variant="solo"
                  color="grey-darken-4"
                  append-inner-icon="mdi-magnify"
                  single-line
                  hide-details
                  class="inventory-field-on-red inventory-toolbar-note-field mr-1 flex-grow-1"
                  @keyup.enter="searchMethod()"
                  @click:append-inner="searchMethod()"
                />
              </div>
              <!--SEARCH OTHER FIELDS-->
              <v-text-field
                v-else
                v-model.trim="search"
                :label="
                  searchType === 'other'
                    ? $t('parts.inventory.search_other')
                    : searchType === 'engineCode'
                      ? $t('parts.inventory.search_engine_code')
                      : $t('parts.inventory.search')
                "
                flat
                variant="solo"
                color="grey-darken-4"
                append-inner-icon="mdi-magnify"
                single-line
                hide-details
                class="inventory-field-on-red"
                @keyup.enter="searchMethod()"
                @click:append-inner="searchMethod()"
              />
            </div>
            </div><!-- end row-1 group -->

            <!-- Row 2 on mobile: type select + advanced button -->
            <div
              class="d-flex align-center ga-2 justify-start justify-lg-end inventory-toolbar-type-row"
            >
              <v-select
                v-model="searchType"
                :items="searchTypes"
                item-title="label"
                item-value="value"
                color="grey-darken-4"
                variant="solo"
                flat
                hide-details
                class="inventory-field-on-red inventory-toolbar-type-select flex-shrink-0 align-self-center"
                :class="{ 'inventory-toolbar-type-select--combo': isComboSearchType }"
              />

              <v-btn
                color="grey-darken-4"
                size="small"
                variant="flat"
                class="flex-shrink-0 inventory-toolbar-advanced-btn"
                @click="advancedSearch = !advancedSearch"
              >
                <v-icon start>
                  {{
                    advancedSearch
                      ? "mdi-checkbox-marked-outline"
                      : "mdi-checkbox-blank-outline"
                  }}
                </v-icon>
                {{ $t("parts.inventory.advanced") }}
              </v-btn>
            </div><!-- end row-2 group -->
          </div>
        </v-col>
      </v-row>
    </div>
    <div class="redColor pa-3 pa-md-4">
      <!--------------------------------------ACTION BUTTONS--------------------------------------->
      <div
        class="d-flex flex-column flex-lg-row align-stretch ga-3 px-md-2 inventory-toolbar-actions"
      >
        <div
          class="d-flex flex-wrap align-center ga-2 justify-center justify-lg-start inventory-toolbar-actions-btns"
        >
          <!-- PDF -->
          <!-- <v-btn
              :loading="loadingPdf"
              :disabled="!$store.getters.institutionEmail
                || $store.getters.institutionEmail.length <= 1
              "
              color="grey-darken-4"
              dark
              @click="exportPdf"
            >
              {{ $t('parts.inventory.export') }}
              <v-icon right>
                fas fa-file-pdf
              </v-icon>
            </v-btn> -->
          <v-btn
            variant="flat"
            dark
            color="grey-darken-4"
            class="inventory-toolbar-action-btn"
            :loading="loadingPdf"
            @click="exportPdf"
          >
            {{ $t("parts.inventory.export") }}
            <v-icon end> mdi-file-pdf-box </v-icon>
          </v-btn>
          <!--Sync. Price-->
          <v-btn
            color="grey-darken-4"
            dark
            :loading="loadingSyncPrice"
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="confirmSyncPrice"
          >
            {{ $t("parts.inventory.sync_price") }}
            <v-icon end> mdi-cached </v-icon>
          </v-btn>
          <v-btn
            v-if="canSyncRrShipping"
            color="grey-darken-4"
            dark
            :loading="loadingSyncRrShipping"
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="confirmSyncRrShipping"
          >
            {{ $t("parts.inventory.sync_rr_shipping") }}
            <v-icon end> mdi-truck-delivery-outline </v-icon>
          </v-btn>
          <v-btn
            v-if="selectedItem"
            :loading="loadingEditMultiple"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="editMultiple"
          >
            {{ $t("parts.inventory.edit_multiple") }}
            <v-icon end small> mdi-pencil </v-icon>
          </v-btn>
          <!--EXCEL-->
          <v-btn
            v-if="selectedItem"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="exportToExcel"
          >
            {{ $t("parts.inventory.export") }}
            <v-icon end>mdi-file-excel-outline</v-icon>
          </v-btn>
          <!--EXCEL-->
          <v-btn
            v-if="selectedItem && $store.getters.institutionExportEbayLeko"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="exportDataEbay"
          >
            {{ $t("parts.inventory.export_ebay") }}
            <v-icon end> mdi-file-excel-outline </v-icon>
          </v-btn>
          <!--Archive-->
          <v-btn
            v-if="selectedItem"
            :loading="loadingArchive"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="exportArchive"
          >
            {{ $t("parts.inventory.export") }}
            <v-icon end> mdi-archive-arrow-down </v-icon>
          </v-btn>

          <!--Share-->
          <v-btn
            v-if="selectedItem"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="share"
          >
            {{ $t("parts.inventory.share") }}
            <v-icon end> mdi-share-variant-outline </v-icon>
          </v-btn>

          <!--Delete-->
          <v-btn
            v-if="
              selectedItem &&
              ($store.getters.role == 'LekoAdmin' ||
                $store.getters.role == 'SuperAdmin')
            "
            color="grey-darken-4"
            dark
            :loading="loadingDeletePart"
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="deleteParts"
          >
            {{ $t("parts.inventory.delete") }}
            <v-icon end> mdi-delete-empty-outline </v-icon>
          </v-btn>

          <!-- <v-btn
              v-if="$store.getters.institutionExportEbayLeko"
              color="grey-darken-4"
              dark
              @click="syncEbay"
            >
              {{ $t('parts.inventory.sync_with_ebay') }}
              <v-icon right>
                sync
              </v-icon>
            </v-btn> -->
          <v-btn
            v-if="selectedItem && $store.getters.institutionExportEbayLeko"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="addEbayItems"
          >
            {{ $t("parts.inventory.add_ebay") }}
            <i class="fa fa-list-ol ml-2 mr-1" />
            <v-icon> mdi-plus </v-icon>
          </v-btn>

          <v-btn
            v-if="
              selectedItem &&
              checkEbayStatus(items) &&
              $store.getters.institutionExportEbayLeko
            "
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="deleteEbayItems"
          >
            {{ $t("parts.inventory.delete_ebay") }}
            <i class="fa fa-list-ol ml-2 mr-1" />
            <v-icon end> mdi-delete-empty-outline </v-icon>
          </v-btn>
          <v-btn
            v-if="selectedItem"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="sellMultiple"
          >
            {{ $t("parts.inventory.sell_multiple") }}
            <i class="fa fa-list-ol ml-2 mr-1" />
            <v-icon> mdi-plus </v-icon>
          </v-btn>
          <v-btn
            v-if="selectedItem && $store.getters.institutionExportGoogleLeko"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="addGoogleItems"
          >
            {{ $t("parts.inventory.add_google") }}
            <i class="fa fa-list-ol ml-2 mr-1" />
            <v-icon> mdi-plus </v-icon>
          </v-btn>
          <v-btn
            v-if="
              selectedItem &&
              checkGoogleStatus(items) &&
              $store.getters.institutionExportEbayLeko
            "
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="deleteGoogleItems"
          >
            {{ $t("parts.inventory.delete_google") }}
            <i class="fa fa-list-ol ml-2 mr-1" />
            <v-icon end> mdi-delete-empty-outline </v-icon>
          </v-btn>
          <!--No Applicability-->
          <v-btn
            v-if="advancedSearch"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="toggleNoApplicabilityDropdown"
          >
            No Appl.
            <v-icon end>
              {{
                noApplicabilityDropdown
                  ? "mdi-checkbox-marked-outline"
                  : "mdi-checkbox-blank-outline"
              }}
            </v-icon>
          </v-btn>
          <!-- Refused parts with no catalog title / OEM -->
          <v-btn
            v-if="advancedSearch"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="toggleNoTitleNoOemFilter"
          >
            {{ $t("parts.advanced_search.no_title_no_oem") }}
            <v-icon end>
              {{
                advancedSearchItems.noTitleNoOem
                  ? "mdi-checkbox-marked-outline"
                  : "mdi-checkbox-blank-outline"
              }}
            </v-icon>
          </v-btn>
          <!-- Position mismatch (SuperAdmin) -->
          <v-btn
            v-if="
              advancedSearch && $store.getters.role === 'SuperAdmin'
            "
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="togglePositionMismatchFilter"
          >
            {{ $t("parts.advanced_search.position_mismatch_btn") }}
            <v-icon end>
              {{
                advancedSearchItems.positionMismatch
                  ? "mdi-checkbox-marked-outline"
                  : "mdi-checkbox-blank-outline"
              }}
            </v-icon>
          </v-btn>
          <!--InfoCar Cad-->
          <v-btn
            v-if="
              advancedSearch &&
              $store.getters.institutionDisplayFeatures.infoCarCad &&
              brand &&
              model &&
              version
            "
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="openInfoCarCad"
          >
            {{ $t("parts.inventory.cad") }}
            <v-icon end> mdi-information-outline </v-icon>
          </v-btn>
          <!--Car Parts List-->
          <v-btn
            v-if="advancedSearch && brand && model && version"
            color="grey-darken-4"
            dark
            variant="flat"
            class="inventory-toolbar-action-btn"
            @click="openCarPartsList"
          >
            {{ $t("parts.inventory.car_parts_list") }}
            <v-icon end> mdi-car-multiple </v-icon>
          </v-btn>
        </div>
        <div
          class="d-flex justify-center justify-lg-end align-lg-start flex-shrink-0"
        >
          <!--SCAN QR-->
          <v-btn
            variant="flat"
            class="inventory-toolbar-action-btn inventory-toolbar-scan-btn text-none"
            color="grey-darken-4"
            dark
            @click="scanQr"
          >
            <v-icon start> mdi-qrcode </v-icon>
            {{ $t("parts.inventory.scan_qr") }}
          </v-btn>
        </div>
      </div>
      <!--------------------------------------ADVANCED SEARCH-------------------------------------->
      <div v-if="advancedSearch" class="inventory-advanced-search-panel">
        <v-form ref="form" lazy-validation>
          <v-row class="inventory-advanced-search-grid pb-2 px-2 px-sm-4 flex-wrap align-center">
            <!--NO APPLICABILITY DROPDOWN-->
            <v-col
              v-if="advancedSearch && noApplicabilityDropdown"
              cols="12"
              class="d-flex"
            >
              <v-row class="ma-2 d-flex flex-wrap align-center ga-4">
                <v-checkbox
                  v-model="advancedSearchItems.noApplicability"
                  :label="$t('parts.advanced_search.no_applicability')"
                  hide-details
                  color="grey-lighten-4"
                />
                <v-checkbox
                  v-model="advancedSearchItems.noNotes"
                  :label="$t('parts.advanced_search.no_notes')"
                  hide-details
                  color="grey-lighten-4"
                />
                <v-checkbox
                  v-model="advancedSearchItems.customApplicability"
                  :label="$t('parts.advanced_search.custom_applicability')"
                  hide-details
                  color="grey-lighten-4"
                />
                <v-checkbox
                  v-if="advancedSearchItems.noApplicability"
                  class="ml-6"
                  :model-value="advancedSearchItems.labelOrigin === 'neutral'"
                  :label="$t('parts.advanced_search.neutral_label')"
                  hide-details
                  color="grey-lighten-4"
                  @update:model-value="setLabelOrigin('neutral', $event)"
                />
                <v-checkbox
                  v-if="advancedSearchItems.noApplicability"
                  class="ml-2"
                  :model-value="advancedSearchItems.labelOrigin === 'vehicle'"
                  :label="$t('parts.advanced_search.vehicle_qr')"
                  hide-details
                  color="grey-lighten-4"
                  @update:model-value="setLabelOrigin('vehicle', $event)"
                />
              </v-row>
            </v-col>
            <!---------------------------------------DIVIDER--------------------------------------->
            <v-col cols="12" lg="12" md="12" xl="12">
              <v-row
                no-gutters
                align="center"
                justify="center"
                class="inventory-advanced-divider-row mt-2 mb-1 mx-1"
              >
                <v-divider
                  class="flex-grow-1 d-none d-sm-flex"
                  style="border-width: 1px; border-color: white; opacity: 100"
                />

                <div
                  class="inventory-advanced-divider-label mx-2 mx-sm-4 px-1 text-white font-weight-medium text-center text-subtitle-1 text-md-h5"
                >
                  {{ $t("parts.advanced_search.car_details") }}
                </div>
                <v-divider
                  class="flex-grow-1 d-none d-sm-flex"
                  style="border-width: 1px; border-color: white; opacity: 100"
                />
              </v-row>
            </v-col>
            <!----------------------------------------PLATE---------------------------------------->
            <v-col cols="12" sm="3" lg="4">
              <!-- <div style="position: relative">
                <v-text-field
                  v-model="plate"
                  flat
                  variant="solo"
                  maxlength="7"
                  :disabled="!canSearchPlates"
                  :color="foundPlate ? 'success' : 'error'"
                  :label="
                    $t('parts.advanced_search.plate') + ' ' + examplePlateFormat
                  "
                  hide-details
                  :append-inner-icon="
                    foundPlate
                      ? 'mdi-check-circle-outline'
                      : 'mdi-alert-circle-outline'
                  "
                  @keyup.enter="searchPlate()"
                  @click:append-inner="searchPlate()"
                  @input="formatPlate"
                />
                <v-progress-linear
                  v-if="loadingPlate"
                  color="black"
                  indeterminate
                  height="3"
                  absolute
                  bottom
                  style="position: absolute; left: 0; right: 0"
                />
              </div> -->
              <!-- <div class="plate-wrapper" style="position: relative">
                <v-text-field
                  v-model="plate"
                  flat
                  variant="solo"
                  maxlength="7"
                  :disabled="!canSearchPlates"
                  :color="foundPlate ? 'success' : 'error'"
                  :label="
                    $t('parts.advanced_search.plate') + ' ' + examplePlateFormat
                  "
                  hide-details
                  :append-inner-icon="
                    foundPlate
                      ? 'mdi-check-circle-outline'
                      : 'mdi-alert-circle-outline'
                  "
                  autocomplete="off"
                  @keyup.enter="searchPlate()"
                  @click:append-inner="searchPlate()"
                  @input="formatPlate"
                  @focus="showDropdown = true"
                  @blur="onBlur"
                />
                <div
                  v-if="showDropdown && lastFivePlates.length"
                  class="plates-dropdown"
                  style="
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #ccc;
                    z-index: 10;
                    max-height: 200px;
                    overflow-y: auto;
                  "
                >
                  <div
                    v-for="(p, index) in lastFivePlates"
                    :key="index"
                    class="plate-item"
                    style="padding: 8px; cursor: pointer"
                    :style="{
                      background: hovered === index ? '#eee' : 'white',
                    }"
                    @mousedown.prevent="selectPlate(p)"
                    @mouseover="hovered = index"
                  >
                    {{ p.toUpperCase() }}
                  </div>
                </div>
              </div> -->
              <v-tooltip v-if="!canSearchPlates" location="top">
                <template #activator="{ props }">
                  <div
                    v-bind="props"
                    class="plate-wrapper"
                    style="position: relative"
                  >
                    <v-text-field
                      v-model="plate"
                      flat
                      variant="solo"
                      maxlength="7"
                      :disabled="!canSearchPlates"
                      :color="foundPlate ? 'success' : 'error'"
                      :label="
                        $t('parts.advanced_search.plate') +
                        ' ' +
                        examplePlateFormat
                      "
                      hide-details
                      :append-inner-icon="
                        foundPlate
                          ? 'mdi-check-circle-outline'
                          : 'mdi-alert-circle-outline'
                      "
                      autocomplete="off"
                    />
                  </div>
                </template>

                <span
                  ><v-icon color="warning">mdi-alert-outline</v-icon>
                  {{ $t("plate_search_disabled_message") }}</span
                >
              </v-tooltip>
              <div v-else class="plate-wrapper" style="position: relative">
                <v-text-field
                  v-model="plate"
                  flat
                  variant="solo"
                  maxlength="7"
                  :disabled="!canSearchPlates"
                  :color="foundPlate ? 'success' : 'error'"
                  :label="
                    $t('parts.advanced_search.plate') + ' ' + examplePlateFormat
                  "
                  hide-details
                  :append-inner-icon="
                    foundPlate
                      ? 'mdi-check-circle-outline'
                      : 'mdi-alert-circle-outline'
                  "
                  autocomplete="off"
                  @keyup.enter="searchPlate()"
                  @click:append-inner="searchPlate()"
                  @input="formatPlate"
                  @focus="showDropdown = true"
                  @blur="onBlur"
                />
                <div
                  v-if="showDropdown && lastFivePlates.length"
                  class="plates-dropdown"
                  style="
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #ccc;
                    z-index: 10;
                    max-height: 200px;
                    overflow-y: auto;
                  "
                >
                  <div
                    v-for="(p, index) in lastFivePlates"
                    :key="index"
                    class="plate-item"
                    style="padding: 8px; cursor: pointer"
                    :style="{
                      background: hovered === index ? '#eee' : 'white',
                    }"
                    @mousedown.prevent="selectPlate(p)"
                    @mouseover="hovered = index"
                  >
                    {{ p.toUpperCase() }}
                  </div>
                </div>
              </div>
              <!-- Normal field when enabled -->
              <!-- <div v-else class="plate-wrapper" style="position: relative">
                <v-text-field
                  v-model="plate"
                  flat
                  variant="solo"
                  maxlength="7"
                  :disabled="!canSearchPlates"
                  :color="foundPlate ? 'success' : 'error'"
                  :label="
                    $t('parts.advanced_search.plate') + ' ' + examplePlateFormat
                  "
                  hide-details
                  :append-inner-icon="
                    foundPlate
                      ? 'mdi-check-circle-outline'
                      : 'mdi-alert-circle-outline'
                  "
                  autocomplete="off"
                  @keyup.enter="searchPlate()"
                  @click:append-inner="searchPlate()"
                  @input="formatPlate"
                  @focus="showDropdown = true"
                  @blur="onBlur"
                />
              </div> -->
              <!-- <v-text-field
                v-model="plate"
                flat
                variant="solo"
                return-masked-value
                :disabled="!canSearchPlates"
                :color="foundPlate ? 'success': 'error'"
                :label="$t('parts.advanced_search.plate') + ' ' + examplePlateFormat"
                :loading="loadingPlate"
                class="ml-2 mr-2 mt-2 custom-loader"
                hide-details
                :append-inner-icon="foundPlate ? 'check_circle': 'error'"
                @keyup.enter="searchPlate()"
                @click:appendInner="searchPlate()"
                @input="formatPlate"
              >
                <template v-slot:progress>
                  <v-progress-linear
                    v-if="loadingPlate"
                    color="red"
                    indeterminate
                  />
                </template>
              </v-text-field> -->
            </v-col>
            <!----------------------------------------BRAND---------------------------------------->
            <v-col cols="12" sm="7" lg="4">
              <v-autocomplete
                v-model="brand"
                :items="brands"
                :loading="loadingBrand"
                :label="$t('parts.advanced_search.brand')"
                item-title="title"
                autocomplete="off"
                flat
                variant="solo"
                color="red-darken-1"
                hide-details
                return-object
                clearable
                @click:append-inner="brand = null"
              >
                <!-- Custom dropdown item -->
                <template #item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template #prepend>
                      <v-img
                        :src="item.raw.logo"
                        height="35"
                        width="35"
                        class="mr-3"
                        cover
                      />
                    </template>
                    <!-- <v-list-item-title>{{ item.raw.title }}</v-list-item-title> -->
                  </v-list-item>
                </template>

                <!-- Selected value in the field -->
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <v-img
                      :src="item.raw.logo"
                      height="35"
                      width="35"
                      class="mr-3"
                      cover
                    />
                    <span>{{ item.raw.title }}</span>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
            <!----------------------------------------YEAR----------------------------------------->
            <v-col cols="12" sm="2" lg="4">
              <v-text-field
                v-model="year"
                :label="$t('parts.advanced_search.year')"
                :rules="[yearRule]"
                flat
                variant="solo"
                type="number"
                :max="new Date().getFullYear()"
                oninput="validity.valid || (value = new Date().getFullYear())"
                color="red-darken-1"
                hide-details
              />
            </v-col>
            <!----------------------------------------MODEL---------------------------------------->
            <v-col cols="12" sm="8" xl="5" lg="4">
              <v-combobox
                v-model="model"
                :items="models"
                :loading="loadingModel"
                :label="!model ? $t('parts.advanced_search.model') : ''"
                item-title="description"
                item-value="modelCode"
                :custom-filter="vehicleTextFilter"
                autocomplete="off"
                variant="solo"
                color="red-darken-1"
                hide-details
                return-object
                clearable
                class="inventory-car-combobox"
                @click:append-inner="model = null"
              >
                <!-- Dropdown items -->
                <template #item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-content>
                      <v-row>
                        <!-- Description -->
                        <v-col cols="12" md="4" class="bg-grey-lighten-3 mt-2">
                          <div class="d-flex flex-column">
                            <span class="caption">{{
                              $t("parts.advanced_search.model")
                            }}</span>
                            <span class="font-weight-bold">{{
                              $sanitizeVehicleText(item.raw.description)
                            }}</span>
                          </div>
                        </v-col>

                        <!-- Year range -->
                        <v-col
                          cols="12"
                          md="8"
                          class="bg-grey-lighten-3 mt-2"
                        >
                          <div class="d-flex flex-column">
                            <span class="caption">{{
                              $t("parts.advanced_search.year")
                            }}</span>
                            <span class="font-weight-bold">
                              {{ formatMonthYear(item.raw.startingDate) }} -
                              {{ formatMonthYear(item.raw.endingDate) }}
                            </span>
                          </div>
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                </template>

                <!-- Selected value -->
                <template #selection="{ item }">
                  <div
                    class="inventory-car-detail-selection d-flex justify-between align-center pa-2 w-100"
                  >
                    <div class="inventory-car-detail-col d-flex flex-column pr-2">
                      <span class="caption">{{
                        $t("parts.advanced_search.model")
                      }}</span>
                      <span class="font-weight-bold">{{
                        $sanitizeVehicleText(item.raw.description)
                      }}</span>
                    </div>
                    <div class="inventory-car-detail-col d-flex flex-column">
                      <span class="caption">{{
                        $t("parts.advanced_search.year")
                      }}</span>
                      <span class="font-weight-bold">
                        {{ formatMonthYear(item.raw.startingDate) }} -
                        {{ formatMonthYear(item.raw.endingDate) }}
                      </span>
                    </div>
                  </div>
                </template>
              </v-combobox>
            </v-col>
            <!---------------------------------------ENGINE---------------------------------------->
            <v-col cols="12" sm="4" xl="4" lg="4">
              <!--------------------------------------SUPPLY--------------------------------------->
              <v-row class="d-block ml-0 ml-sm-2 flex-column">
                <div class="subheading text-white">
                  {{ $t("parts.advanced_search.supply.supply") }}
                </div>
                <v-radio-group
                  v-model="supply"
                  class="ma-0"
                  inline
                  hide-details
                >
                  <v-radio
                    color="grey-lighten-4"
                    :label="supplies[0].title"
                    :value="supplies[0].code"
                  />
                  <v-radio
                    color="grey-lighten-4"
                    :label="supplies[1].title"
                    :value="supplies[1].code"
                  />
                  <v-radio
                    color="grey-lighten-4"
                    :label="supplies[2].title"
                    :value="supplies[2].code"
                  />
                </v-radio-group>
              </v-row>
            </v-col>
            <v-col cols="12" sm="4" xl="4" lg="4">
              <v-autocomplete
                v-model="engine"
                :items="versions"
                :loading="loadingVersion"
                :label="$t('parts.advanced_search.engine')"
                item-title="engineCode"
                autocomplete="off"
                flat
                variant="solo"
                color="red-darken-1"
                hide-details
                clearable
                @click:append-inner="
                  engine = null;
                  version = null;
                  if (model) getVersions(model.brandCode, model.modelCode);
                "
              />
            </v-col>
            <!---------------------------------------VERSION--------------------------------------->
            <v-col cols="12" xl="12" lg="12" md="12">
              <v-combobox
                v-model="version"
                :items="versions"
                :loading="loadingVersion"
                :label="!version ? $t('parts.advanced_search.version') : ''"
                item-title="fullDescription"
                item-value="versionCode"
                autocomplete="off"
                :custom-filter="vehicleTextFilter"
                flat
                variant="solo"
                color="red-darken-1"
                hide-details
                return-object
                clearable
                class="inventory-car-combobox inventory-version-combobox"
                @click:append-inner="version = null"
              >
                <!-- Dropdown items -->
                <template #item="{ item, props }">
                  <v-list-item v-bind="props">
                    <div
                      class="d-sm-none inventory-version-detail-item bg-grey-lighten-3 mt-2 pa-3 w-100"
                    >
                      <div
                        class="inventory-version-detail-row inventory-version-detail-row--dropdown"
                      >
                        <div
                          class="inventory-version-detail-col inventory-version-detail-col--main"
                        >
                          <span class="caption">{{
                            $t("parts.advanced_search.version")
                          }}</span>&nbsp;
                          <span class="font-weight-bold">{{
                            $sanitizeVehicleText(item.raw.description)
                          }}</span>
                        </div>
                        <div class="inventory-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.year")
                          }}</span>&nbsp;
                          <span class="font-weight-bold">
                            {{ formatMonthYear(item.raw.startingDate) }} -
                            {{ formatMonthYear(item.raw.endingDate) }}
                          </span>
                        </div>
                        <div class="inventory-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.body")
                          }}</span>&nbsp;
                          <span class="font-weight-bold">{{
                            item.raw.numberOfDoors
                          }}</span>
                        </div>
                        <div class="inventory-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.power")
                          }}</span>&nbsp;
                          <span class="font-weight-bold">
                            {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                          </span>
                        </div>
                        <div class="inventory-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.engine")
                          }}</span>&nbsp;
                          <span class="font-weight-bold">{{
                            item.raw.engineCode
                          }}</span>
                        </div>
                        <div class="inventory-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.supply.supply")
                          }}</span>&nbsp;
                          <span class="font-weight-bold">{{
                            getSupplyLocale(item.raw.supplyCode)
                          }}</span>
                        </div>
                      </div>
                    </div>
                    <v-row class="d-none d-sm-flex pa-3 bg-grey-lighten-3 mt-2">
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.version")
                          }}</span>
                          <span class="font-weight-bold">{{
                            $sanitizeVehicleText(item.raw.description)
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.year")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ formatMonthYear(item.raw.startingDate) }} -
                            {{ formatMonthYear(item.raw.endingDate) }}
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.body")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.numberOfDoors
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.power")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.engine")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.engineCode
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.supply.supply")
                          }}</span>
                          <span class="font-weight-bold">{{
                            getSupplyLocale(item.raw.supplyCode)
                          }}</span>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-list-item>
                </template>

                <!--SELECTED-->
                <template #selection="{ item }">
                  <div
                    class="inventory-version-detail-selection inventory-car-detail-selection w-100 d-sm-none"
                  >
                    <div class="inventory-version-detail-row">
                      <div
                        class="inventory-version-detail-col inventory-version-detail-col--main"
                      >
                        <span class="caption">
                          {{ $t("parts.advanced_search.version") }}
                        </span>
                        <span class="font-weight-bold">{{
                          $sanitizeVehicleText(item.raw.description)
                        }}</span>
                      </div>
                      <div class="inventory-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.year")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ formatMonthYear(item.raw.startingDate) }} -
                          {{ formatMonthYear(item.raw.endingDate) }}
                        </span>
                      </div>
                      <div class="inventory-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.body")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.numberOfDoors
                        }}</span>
                      </div>
                      <div class="inventory-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.power")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ item.raw.power ? item.raw.power.kw : " " }} kw,
                        </span>
                      </div>
                      <div class="inventory-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.engine")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.engineCode
                        }}</span>
                      </div>
                      <div class="inventory-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.supply.supply")
                        }}</span>
                        <span class="font-weight-bold">{{
                          getSupplyLocale(item.raw.supplyCode)
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <v-row class="pa-2 d-none d-sm-flex">
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">
                          {{ $t("parts.advanced_search.version") }}
                        </span>
                        <span class="font-weight-bold">{{
                          $sanitizeVehicleText(item.raw.description)
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.year")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ formatMonthYear(item.raw.startingDate) }} -
                          {{ formatMonthYear(item.raw.endingDate) }}
                        </span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.body")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.numberOfDoors
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.power")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ item.raw.power ? item.raw.power.kw : " " }} kw,
                        </span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.engine")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.engineCode
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.supply.supply")
                        }}</span>
                        <span class="font-weight-bold">{{
                          getSupplyLocale(item.raw.supplyCode)
                        }}</span>
                      </v-row>
                    </v-col>
                  </v-row>
                </template>
              </v-combobox>
            </v-col>
            <!---------------------------------------DIVIDER--------------------------------------->
            <v-col cols="12" lg="12" md="12" xl="12">
              <v-row
                no-gutters
                align="center"
                justify="center"
                class="inventory-advanced-divider-row mt-2 mb-1 mx-1"
              >
                <v-divider
                  class="flex-grow-1 d-none d-sm-flex"
                  style="border-width: 1px; border-color: white; opacity: 100"
                />

                <div
                  class="inventory-advanced-divider-label mx-2 mx-sm-4 px-1 text-white font-weight-medium text-center text-subtitle-1 text-md-h5"
                >
                  {{ $t("parts.advanced_search.part_details") }}
                </div>
                <v-divider
                  class="flex-grow-1 d-none d-sm-flex"
                  style="border-width: 1px; border-color: white; opacity: 100"
                />
              </v-row>
            </v-col>
            <!------------------------------------INSTITUTIONS------------------------------------->
            <v-col
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'LekoEmployee' ||
                $store.getters.role === 'SuperAdmin'
              "
              cols="12"
              sm="12"
              md="12"
              lg="12"
              xl="12"
              class="mt-0 mb-0 pt-0 pb-0"
            >
              <v-autocomplete
                v-model="advancedSearchItems.institution"
                :items="institutions"
                :loading="loadingInstitution"
                :label="$t('parts.advanced_search.institution')"
                :hint="$t('parts.advanced_search.institution')"
                persistent-hint
                autocomplete="off"
                clearable
                item-title="title"
                item-value="_id"
                flat
                variant="solo"
                color="red-darken-1"
              />
            </v-col>
            <!--------------------------------------SHIPMENTS-------------------------------------->

            <v-col
              cols="12"
              :sm="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'LekoEmployee' ||
                $store.getters.role === 'SuperAdmin'
                  ? 6
                  : 12
              "
              lg="3"
              xl="2"
            >
              <v-autocomplete
                v-model="advancedSearchItems.shipment"
                :items="shipments"
                :loading="loadingShipment"
                :label="$t('parts.advanced_search.shipment')"
                :hint="$t('parts.advanced_search.shipment')"
                persistent-hint
                item-title="name"
                item-value="_id"
                autocomplete="off"
                flat
                variant="solo"
                color="red-darken-1"
              />
            </v-col>
            <!----------------------------------------PRICE---------------------------------------->
            <v-col cols="6" sm="6" md="3" lg="2" xl="2">
              <v-combobox
                v-model="advancedSearchItems.price[0]"
                :items="prices"
                flat
                variant="solo"
                :label="$t('parts.inventory.from')"
                :hint="$t('parts.inventory.from')"
                persistent-hint
                color="red-darken-1"
                prepend-icon="mdi-currency-eur"
                type="number"
                oninput="validity.valid || (value = '')"
              />
            </v-col>
            <v-col cols="6" sm="6" md="3" lg="2" xl="2">
              <v-combobox
                v-model="advancedSearchItems.price[1]"
                :items="prices"
                variant="solo"
                :label="$t('parts.inventory.upto')"
                :hint="$t('parts.inventory.upto')"
                persistent-hint
                flat
                color="red-darken-1"
                prepend-icon="mdi-currency-eur"
                type="number"
                oninput="validity.valid || (value = '')"
              />
            </v-col>
            <!----------------------------------------DATE----------------------------------------->
            <!-- STARTING DATE -->
            <v-col cols="12" sm="6" md="3" lg="3" xl="3">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290"
              >
                <template #activator="{ props }">
                  <v-text-field
                    v-model="advancedSearchItems.startDate"
                    :value="formatDateToDisplay(advancedSearchItems.startDate)"
                    :label="$t('parts.advanced_search.starting_date')"
                    :hint="$t('parts.advanced_search.select_starting_date')"
                    variant="solo"
                    flat
                    color="black"
                    persistent-hint
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="props"
                  />
                </template>

                <v-date-picker
                  v-model="advancedSearchItems.startDate"
                  color="red-darken-3"
                  :max="advancedSearchItems.endDate || null"
                  @update:model-value="startDateMenu = false"
                />
              </v-menu>
            </v-col>

            <!-- ENDING DATE -->
            <v-col cols="12" sm="6" md="3" lg="2" xl="2">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290"
              >
                <template #activator="{ props }">
                  <v-text-field
                    v-model="advancedSearchItems.endDate"
                    :value="formatDateToDisplay(advancedSearchItems.endDate)"
                    :label="$t('parts.advanced_search.ending_date')"
                    :hint="$t('parts.advanced_search.select_ending_date')"
                    variant="solo"
                    flat
                    color="black"
                    persistent-hint
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="props"
                  />
                </template>

                <v-date-picker
                  v-model="advancedSearchItems.endDate"
                  color="red-darken-3"
                  :min="advancedSearchItems.startDate || null"
                  @update:model-value="endDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6" lg="3" xl="2">
              <v-autocomplete
                v-model="advancedSearchItems.repository"
                :items="repositories"
                :label="$t('parts.advanced_search.select_repository')"
                :hint="$t('parts.advanced_search.select_repository')"
                persistent-hint
                autocomplete="off"
                clearable
                item-title="name"
                item-value="_id"
                flat
                variant="solo"
                color="red-darken-1"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3" xl="2">
              <v-autocomplete
                v-model="advancedSearchItems.room"
                :items="advancedSearchRooms"
                :disabled="!advancedSearchItems.repository"
                :label="$t('parts.inventory.form.select_room')"
                :hint="$t('parts.inventory.form.select_room')"
                persistent-hint
                autocomplete="off"
                clearable
                item-title="name"
                item-value="_id"
                flat
                variant="solo"
                color="red-darken-1"
              />
            </v-col>
            <v-col cols="12" sm="6" lg="3" xl="2">
              <v-autocomplete
                v-model="advancedSearchItems.location"
                :items="advancedSearchLocations"
                :disabled="!advancedSearchItems.room"
                :label="$t('parts.inventory.form.select_location')"
                :hint="$t('parts.inventory.form.select_location')"
                persistent-hint
                autocomplete="off"
                clearable
                item-title="code"
                item-value="_id"
                flat
                variant="solo"
                color="red-darken-1"
              />
            </v-col>
            <!-- CONDITIONAL CHECKBOXES -->

            <!-- SEARCH BY EBAY STATUS -->
            <v-col
              v-if="
                $store.getters.role == 'LekoAdmin' ||
                $store.getters.role == 'SuperAdmin' ||
                $store.getters.institutionExportEbayLeko
              "
              cols="12"
              lg="4"
              md="4"
              xl="4"
            >
              <v-row class="ml-0 ml-sm-2 mt-2 flex-column">
                <div class="subheading text-white">
                  {{ $t("parts.advanced_search.filter_for") }}
                  <strong>{{ $t("parts.advanced_search.ebay_status") }}</strong>
                </div>
                <!-- Mobile: dropdown -->
                <v-select
                  v-if="$vuetify.display.xs"
                  v-model="advancedSearchItems.eBayStatus"
                  :disabled="
                    !$store.getters.institutionExportEbayLeko &&
                    $store.getters.role != 'LekoAdmin' &&
                    $store.getters.role != 'SuperAdmin'
                  "
                  :items="[
                    { title: $t('parts.advanced_search.select_all'), value: '' },
                    { title: $t('parts.advanced_search.on_ebay'), value: true },
                    { title: $t('parts.advanced_search.not_on_ebay'), value: false },
                  ]"
                  item-title="title"
                  item-value="value"
                  hide-details
                  density="compact"
                  variant="outlined"
                  bg-color="red-darken-3"
                  class="mt-1"
                />
                <!-- Desktop: radio group -->
                <div v-else>
                  <v-radio-group
                    v-model="advancedSearchItems.eBayStatus"
                    :inline="$vuetify.display.smAndUp"
                    dense
                    hide-details
                    class="mr-0 pr-0"
                  >
                    <v-radio
                      :disabled="
                        !$store.getters.institutionExportEbayLeko &&
                        $store.getters.role != 'LekoAdmin' &&
                        $store.getters.role != 'SuperAdmin'
                      "
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.select_all')"
                      value=""
                    />
                    <v-radio
                      :disabled="
                        !$store.getters.institutionExportEbayLeko &&
                        $store.getters.role != 'LekoAdmin' &&
                        $store.getters.role != 'SuperAdmin'
                      "
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.on_ebay')"
                      :value="true"
                    />
                    <v-radio
                      :disabled="
                        !$store.getters.institutionExportEbayLeko &&
                        $store.getters.role != 'LekoAdmin' &&
                        $store.getters.role != 'SuperAdmin'
                      "
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.not_on_ebay')"
                      :value="false"
                    />
                  </v-radio-group>
                </div>
                <div
                  v-if="advancedSearchItems.eBayStatus === false"
                  class="d-flex ml-0 mt-3"
                >
                  <v-checkbox
                    v-model="advancedSearchItems.no_shipping_cost"
                    :label="$t('parts.advanced_search.no_shipping_cost')"
                    dark
                    class="ma-0 pa-0"
                    color="grey-lighten-4"
                  />
                </div>
              </v-row>
              <!-- <div
                v-if="advancedSearchItems.eBayStatus === false"
                class="d-flex ml-2 mt-1"
              >
                <v-checkbox
                  v-model="advancedSearchItems.no_shipping_cost"
                  :label="$t('parts.advanced_search.no_shipping_cost')"
                  dark
                  class="ma-0 pa-0"
                  color="grey-lighten-4"
                />
                <v-checkbox
                  v-model="advancedSearchItems.invalid_category"
                  :label="$t('parts.advanced_search.invalid_category')"
                  dark
                  class="ma-0 pa-0"
                  color="grey-lighten-4"
                />
              </div> -->
            </v-col>
            <!-- SEARCH BY PART CONDITION  -->
            <v-col cols="12" lg="4" md="4" xl="4">
              <v-row class="d-block ml-0 ml-sm-2 mt-2 flex-column">
                <div class="subheading text-white">
                  {{ $t("parts.advanced_search.filter_for") }}
                  <strong>{{
                    $t("parts.advanced_search.part_condition")
                  }}</strong>
                </div>
                <!-- Mobile: dropdown -->
                <v-select
                  v-if="$vuetify.display.xs"
                  v-model="advancedSearchItems.isNewPart"
                  :items="[
                    { title: $t('parts.advanced_search.select_all'), value: '' },
                    { title: $t('parts.advanced_search.new'), value: true },
                    { title: $t('parts.advanced_search.used'), value: false },
                  ]"
                  item-title="title"
                  item-value="value"
                  hide-details
                  density="compact"
                  variant="outlined"
                  bg-color="red-darken-3"
                  class="mt-1"
                />
                <!-- Desktop: radio group -->
                <div v-else>
                  <v-radio-group
                    v-model="advancedSearchItems.isNewPart"
                    :inline="$vuetify.display.smAndUp"
                    hide-details
                    class="mr-0 pr-0"
                  >
                    <v-radio
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.select_all')"
                      value=""
                    />
                    <v-radio
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.new')"
                      :value="true"
                    />
                    <v-radio
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.used')"
                      :value="false"
                    />
                  </v-radio-group>
                </div>
              </v-row>
            </v-col>
            <!---------------------------------------POSITION---------------------------------------->
            <v-col cols="12" sm="6" class="pa-2">
              <!-- <v-col v-if="searchType != 'id'" xs="12" lg="4" md="4" xl="4"> -->
              <v-row class="ml-0 ml-sm-2 mt-2 d-block flex-column">
                <div class="subheading text-white">
                  {{ $t("parts.advanced_search.select_position") }}
                </div>
                <!-- Mobile: dropdowns -->
                <template v-if="$vuetify.display.xs">
                  <v-select
                    v-model="advancedSearchItems.positionCode"
                    :items="[
                      { title: $t('parts.advanced_search.select_all'), value: '' },
                      { title: 'Neutro', value: 'S000' },
                      { title: 'SX', value: 'S001' },
                      { title: 'DX', value: 'S002' },
                    ]"
                    item-title="title"
                    item-value="value"
                    hide-details
                    density="compact"
                    variant="outlined"
                    bg-color="red-darken-3"
                    class="mt-1 mb-2"
                  />
                  <v-select
                    v-if="searchType === 'pra' || searchType === 'other'"
                    v-model="advancedSearchItems.position"
                    :items="[
                      { title: $t('parts.advanced_search.select_all'), value: '' },
                      { title: 'Ant.', value: 'ant.' },
                      { title: 'Post.', value: 'post.' },
                    ]"
                    item-title="title"
                    item-value="value"
                    hide-details
                    density="compact"
                    variant="outlined"
                    bg-color="grey-darken-3"
                  />
                </template>
                <!-- Desktop: radio groups -->
                <template v-else>
                  <v-radio-group
                    v-model="advancedSearchItems.positionCode"
                    :inline="$vuetify.display.smAndUp"
                    hide-details
                  >
                    <v-radio
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.select_all')"
                      value=""
                    />
                    <v-radio color="grey-lighten-4" label="Neutro" value="S000" />
                    <v-radio color="grey-lighten-4" label="SX" value="S001" />
                    <v-radio color="grey-lighten-4" label="DX" value="S002" />
                  </v-radio-group>
                  <v-radio-group
                    v-model="advancedSearchItems.position"
                    :inline="$vuetify.display.smAndUp"
                    hide-details
                  >
                    <v-radio
                      v-if="searchType === 'pra' || searchType === 'other'"
                      color="grey-lighten-4"
                      :label="$t('parts.advanced_search.select_all')"
                      value=""
                    />
                    <v-radio
                      v-if="searchType === 'pra' || searchType === 'other'"
                      color="grey-lighten-4"
                      label="Ant."
                      value="ant."
                    />
                    <v-radio
                      v-if="searchType === 'pra' || searchType === 'other'"
                      color="grey-lighten-4"
                      label="Post."
                      value="post."
                    />
                  </v-radio-group>
                </template>
              </v-row>
             
            </v-col>
            <!---------------------------------------QUALITY--------------------------------------->
            <v-col cols="12" sm="6" class="pa-2">
              <div class="subheading text-white mt-2 mb-2">
                {{ $t("parts.advanced_search.select_quality") }}
              </div>
              <div class="d-flex align-center gap-2 mt-1">
                <v-rating
                  :model-value="selectedQualityRating"
                  length="5"
                  color="yellow-darken-2"
                  active-color="yellow"
                  empty-icon="mdi-star-outline"
                  full-icon="mdi-star"
                  size="32"
                  @update:model-value="onQualityRatingSelect"
                />
                <v-btn
                  v-if="selectedQualityRating > 0"
                  icon
                  variant="text"
                  size="small"
                  color="grey-lighten-2"
                  @click="onQualityRatingSelect(0)"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-if="selectedQualityRating > 0" class="text-grey-lighten-2 text-caption mt-1">
                {{ selectedQualityRating }} {{ selectedQualityRating === 1 ? 'star' : 'stars' }}
              </div>
            
            </v-col>
             <v-col cols="12" class="pa-2">
             <div class="subheading text-white mt-2 mb-2">
                {{ $t("parts.advanced_search.select_status") }}
              </div>
              <v-select
                v-model="advancedSearchItems.status"
                :items="statusItems"
                item-title="title"
                item-value="value"
                multiple
                chips
                closable-chips
                hide-details
                density="compact"
                variant="outlined"
                bg-color="red-darken-3"
                class="mt-1"
              />
            </v-col>
          </v-row>
        </v-form>
        <!----------------------------------------------------------------------------------------->
        <v-row class="justify-center align-center pt-1 pb-4 px-2 flex-wrap ga-2">
          <v-btn
            dark
            color="grey-darken-4"
            class="mx-1"
            @click="clearSearchFields"
          >
            {{ $t("parts.advanced_search.clear") }}
            <v-icon end> mdi-notification-clear-all </v-icon>
          </v-btn>
          <v-btn dark color="grey-darken-4" class="mx-1" @click="searchMethod">
            {{ $t("parts.advanced_search.search") }}
            <v-icon end> mdi-magnify </v-icon>
          </v-btn>
        </v-row>
      </div>
    </div>
    <v-dialog v-model="errorModal" persistent width="400">
      <v-card class="pa-3">
        <v-alert
          :model-value="true"
          type="error"
          variant="tonal"
          border="bottom"
        >
          <strong> {{ errorMsg }}</strong>
        </v-alert>
        <v-card-actions class="col-12 justify-end mt-2">
          <v-btn small @click="closeErrorModal()"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmDialog" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            type="warning"
            variant="tonal"
            border="bottom"
          >
            {{ $t("confirm_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn text variant="outlined" @click="confirmDialog = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn
            color="warning"
            variant="outlined"
            :loading="loadingSyncPrice"
            @click="syncPrice()"
          >
            {{ $t("yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmSyncRrDialog" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            type="warning"
            variant="tonal"
            border="bottom"
          >
            {{ $t("parts.inventory.sync_rr_shipping_confirm") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn text variant="outlined" @click="confirmSyncRrDialog = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn
            color="warning"
            variant="outlined"
            :loading="loadingSyncRrShipping"
            @click="syncRrShipping()"
          >
            {{ $t("yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* eslint-disable no-nested-ternary */

import moment from "moment";
import axios from "axios";
import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";
import { vehicleTextFilter } from "../../utils/sanitizeText";

import * as XLSX from "xlsx";

export default {
  components: {},
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },
  props: ["items", "selected"], // eslint-disable-line vue/require-prop-types
  data: () => ({
    loadingArchive: false,
    loadingPdf: false,
    loadingSyncPrice: false,
    loadingSyncRrShipping: false,
    loadingDeletePart: false,
    searchTypes: [
      { label: "Title", value: "title" },
      { label: "Title + Code ", value: "titleAndCode" },
      { label: "Other", value: "other" },
      { label: "Multiple Titles", value: "multipleTitles" },
      { label: "Multiple Titles + Notes", value: "multipleTitlesNote" },
      {
        label: "Multiple Titles + General Search",
        value: "multipleTitlesOther",
      },
      { label: "ID", value: "id" },
      { label: "PRA", value: "pra" },
      { label: "Engine code", value: "engineCode" },
    ],
    searchType: "title",
    advancedSearch: false,
    search: "",
    foundPlate: false,
    canSearchPlates: false,
    plate: null,
    examplePlateFormat: ": (DG853SV)",
    searchTitle: "",
    searchMultipleTitles: null,
    selectedGroup: null,
    titleCode: null,
    titleCodes: [],
    titles: [],
    brands: [],
    models: [],
    versions: [],
    titleGroups: [],
    brand: null,
    model: null,
    version: null,
    engine: null,
    year: null,
    yearRule: "",
    supply: null,
    supplies: [
      { code: "B", title: "Petrol" },
      { code: "D", title: "Diesel" },
      { code: "G", title: "LPG" },
    ],
    advancedSearchItems: {
      brand: null,
      model: null,
      version: null,
      institution: null,
      shipment: null,
      status: [1, 2, 2.1, 3, 4],
      quality: [],
      price: [0, 5000],
      startDate: "",
      endDate: "",
      noApplicability: false,
      noNotes: false,
      noTitleNoOem: false,
      customApplicability: false,
      labelOrigin: "",
      positionMismatch: false,
      positionCode: "",
      eBayStatus: "",
      no_shipping_cost: false,
      invalid_category: true,
      isNewPart: "",
      position: "",
      repository: null,
      room: null,
      location: null,
    },
    noApplicabilityDropdown: false,
    statusItems: [
      { title: "Entered", value: 1 },
      { title: "Returned", value: 2 },
      { title: "Returned-Shop", value: 2.1 },
      { title: "Re-Entered", value: 3 },
      { title: "Found", value: 4 },
    ],
    qualityItems: [],
    selectedQualityRating: 0,
    institutions: [],
    shipments: [],
    loadingTitle: false,
    loadingMultipleTitles: false,
    loadingBrand: false,
    loadingModel: false,
    loadingVersion: false,
    loadingQuality: false,
    loadingInstitution: false,
    loadingShipment: false,
    loadingPlate: false,
    startDateMenu: false,
    endDateMenu: false,
    prices: [],
    preventAutoComplete: false,
    titleNote: "",
    note: "",
    groupGeneralSearch: "",
    code: "",
    loadingEditMultiple: false,
    // baseUrl: 'http://localhost:3000',
    baseUrl: "https://api.lekotech.com",
    errorModal: false,
    errorMsg: "",
    menuOpen: false,
    searchTest: "",
    // items: [],
    selectedItemTest: null,
    selectingItem: false,
    menuMultipleOpen: false,
    selectingItemCodes: false,
    confirmDialog: false,
    confirmSyncRrDialog: false,
    showDropdown: false,
    hovered: -1,
    logs: [],
    logsCount: null,
    lastFivePlates: [],
    repositories: [],
    repository: null,
  }),
  computed: {
    selectedItem() {
      return this.$props.selected;
    },
    server() {
      return this.$serverUri;
    },
    advancedSearchRooms() {
      const repository = this.repositories.find(
        (item) => item._id === this.advancedSearchItems.repository,
      );
      return repository?.rooms || [];
    },
    advancedSearchLocations() {
      const room = this.advancedSearchRooms.find(
        (item) => item._id === this.advancedSearchItems.room,
      );
      return room?.locations || [];
    },
    canSyncRrShipping() {
      return (
        this.$store.getters.role === "InstitutionAdmin" &&
        this.$store.getters.institutionRr === true
      );
    },
    inventorySearchField() {
      return this.searchType === "multipleTitlesOther"
        ? this.groupGeneralSearch
        : this.search;
    },
    isComboSearchType() {
      return (
        this.searchType === "titleAndCode"
        || this.searchType === "multipleTitlesNote"
        || this.searchType === "multipleTitlesOther"
      );
    },
  },
  watch: {
    search(value) {
      if (
        !value
        && this.advancedSearch === false
        && this.searchType !== "multipleTitlesOther"
      ) {
        this.$emit("clear-search");
      }
    },
    async searchTitle(newValue) {
      if (this.selectingItem) {
        this.selectingItem = false;
        return;
      }
      if (newValue) {
        if (this.selectingItem) {
          this.selectingItem = false;
          return;
        }
        this.loadingTitle = true;
        try {
          const response = await getRequest(
            "/info-car/parts",
            `title=${encodeURIComponent(newValue)}&language=${this.$store.getters.language}`,
          );
          this.titles = response.parts || [];
        } catch (error) {
          this.titles = [];
        } finally {
          this.loadingTitle = false;
          this.menuOpen = this.titles.length > 0;
        }
      } else {
        if (!this.titleCode) {
          if (!this.advancedSearch) this.$emit("clear-search");
          this.titles = [];
          this.menuOpen = false;
        } else {
          const selected = this.titles.find(
            (t) => t.partCode === this.titleCode,
          );
          if (selected) {
            this.selectingItem = true; // prevent re-trigger
            this.searchTitle =
              selected.title?.[this.$store.getters.language] || "";
            this.menuOpen = false;
          }
        }
      }
    },
    searchMultipleTitles(value) {
      if (value) {
        if (this.selectingItemCodes) {
          this.selectingItemCodes = false;
          return;
        }
        this.loadingMultipleTitles = true;
        getRequest(
          "/info-car/partgroups",
          `title=${value}&language=${this.$store.getters.language}`,
        )
          .then((response) => {
            this.titleGroups = response.partgroups;
          })
          .catch(() => {})
          .finally(() => {
            this.loadingMultipleTitles = false;
            if (!this.selectingItemCodes) {
              this.menuMultipleOpen = this.titleGroups.length > 0;
            }
          });
      } else {
        const noGroupSelected =
          !this.titleCodes
          || (Array.isArray(this.titleCodes) && this.titleCodes.length === 0);
        if (noGroupSelected) {
          const keepResults =
            this.searchType === "multipleTitlesOther"
            && this.groupGeneralSearch;
          if (!this.advancedSearch && !keepResults) {
            this.$emit("clear-search");
          }
          this.titleCodes = [];
          this.menuMultipleOpen = false;
        } else {
          const selected = this.titleGroups.find(
            (t) => JSON.stringify(t.codes) === JSON.stringify(this.titleCodes),
          );
          if (selected) {
            this.selectingItemCodes = true; // prevent re-trigger
            this.searchMultipleTitles =
              selected.title?.[this.$store.getters.language] || "";
            this.menuMultipleOpen = false;
          }
        }
      }
    },
    searchType() {
      this.titleCode = null;
      this.search = "";
      this.groupGeneralSearch = "";
      this.titleCodes = [];
      this.selectedGroup = null;
      this.note = "";
      this.code = "";
    },
    advancedSearch(value) {
      if (!value) {
        this.advancedSearchItems = {
          brand: null,
          model: null,
          version: null,
          institution: null,
          shipment: null,
          status: [1, 2, 2.1, 3, 4],
          quality: [],
          price: [0, 5000],
          startDate: "",
          endDate: "",
          noApplicability: false,
          noNotes: false,
          noTitleNoOem: false,
          customApplicability: false,
          labelOrigin: "",
          positionCode: "",
          eBayStatus: "",
          isNewPart: "",
          position: "",
          repository: null,
          room: null,
          location: null,
          // no_shipping_cost: true,
          // invalid_category: true,
        };
        this.brands = [];
        this.models = [];
        this.versions = [];
        this.brand = null;
        this.model = null;
        this.version = null;
      } else {
        this.getQualities();
        this.getBrands();
        this.getInstitution();
        if (
          this.$store.getters.role === "LekoAdmin" ||
          this.$store.getters.role === "LekoEmployee" ||
          this.$store.getters.role === "SuperAdmin"
        ) {
          this.getInstitutions();
        } else {
          this.getShipments(this.$store.getters.institutionId);
        }
      }
    },
    "advancedSearchItems.institution": {
      handler(value) {
        if (value) this.getShipments(value);
      },
    },
    "advancedSearchItems.repository"(value, oldValue) {
      if (value === oldValue) return;
      this.advancedSearchItems.room = null;
      this.advancedSearchItems.location = null;
    },
    "advancedSearchItems.room"(value, oldValue) {
      if (value === oldValue) return;
      this.advancedSearchItems.location = null;
    },
    "advancedSearchItems.noApplicability": {
      handler(value) {
        if (!value) this.advancedSearchItems.labelOrigin = "";
        if (
          !value
          && !this.advancedSearchItems.noNotes
          && !this.advancedSearchItems.customApplicability
        ) {
          this.noApplicabilityDropdown = false;
        }
      },
    },
    "advancedSearchItems.noNotes": {
      handler(value) {
        if (
          !value
          && !this.advancedSearchItems.noApplicability
          && !this.advancedSearchItems.customApplicability
        ) {
          this.noApplicabilityDropdown = false;
        }
      },
    },
    "advancedSearchItems.customApplicability": {
      handler(value) {
        if (value) {
          this.advancedSearchItems.noApplicability = false;
          this.advancedSearchItems.noNotes = false;
          this.advancedSearchItems.labelOrigin = "";
        } else if (
          !this.advancedSearchItems.noApplicability
          && !this.advancedSearchItems.noNotes
        ) {
          this.noApplicabilityDropdown = false;
        }
      },
    },
    "advancedSearchItems.no_shipping_cost": {
      handler(value) {
        if (!value) {
          this.searchMethod();
        }
      },
    },
    "advancedSearchItems.invalid_category": {
      handler(value) {
        if (!value) {
          this.searchMethod();
        }
      },
    },
    brand(value) {
      if (value) {
        this.models = [];
        this.model = null;
        this.versions = [];
        this.version = null;
        // Make sure that we don't get the models if we are searching for a license plate.
        if (this.loadingPlate === false) this.getModels(value.brandCode);
        this.advancedSearchItems.brand = value.brandCode;
      } else {
        this.advancedSearchItems.brand = null;
      }
    },
    model(value) {
      if (typeof value === "string") {
        // Vuetify combobox emits the typed text on every keystroke.
        this.advancedSearchItems.model = null;
      } else if (value) {
        this.versions = [];
        this.version = null;
        // Make sure that we don't get the versions if we are searching for a license plate.
        if (this.loadingPlate === false)
          this.getVersions(value.brandCode, value.modelCode);
        this.advancedSearchItems.model = value.modelCode;
        // Set year rule.
        this.setYearRule(value.startingDate, value.endingDate);
      } else {
        this.advancedSearchItems.model = null;
      }
    },
    version(value) {
      if (typeof value === "string") {
        this.advancedSearchItems.version = null;
      } else if (value) {
        this.advancedSearchItems.version = value.versionCode;
        // Set year rule.
        this.setYearRule(value.startingDate, value.endingDate);
      } else {
        this.advancedSearchItems.version = null;
      }
    },
    engine(value) {
      if (!value) {
        if (this.model && this.model.modelCode) {
          this.getVersions(this.model.brandCode, this.model.modelCode);
        }
        return;
      }
      this.versions = this.versions.filter((v) => v.engineCode === value);
    },
    year(value) {
      if (value) {
        this.$refs.form.validate();
        // filter models
        if (value.length === 4) {
          // Filter only when year is valid (4 characters long)
          this.filterModels();
          this.filterVersions();
        } else if (value.length !== 4 && this.brand && this.brand.brandCode) {
          // Reset models.
          this.getModels(this.brand.brandCode);
        }
      }
    },
    supply() {
      if (
        this.brand &&
        this.brand.brandCode &&
        this.model &&
        this.model.modelCode
      ) {
        // reset versions.
        this.getVersions(this.brand.brandCode, this.model.modelCode);
      }
    },
    "$i18n.locale"() {
      this.getLocale();
    },
  },
  mounted() {
    this.getLocale();
    // Generate prices list.
    this.generatePricesList();
    // Set initial year rule.
    this.setYearRule();
    if (this.$store.getters.userId) {
      this.getLogs(this.$store.getters.userId);
    }
    this.getRepositories();
    if (this.$route.query && this.$route.query.noappl) {
      this.advancedSearch = true;
      this.toggleNoApplicabilityDropdown();
      this.advancedSearchItems.noApplicability = true;
      this.advancedSearchItems.noNotes = false;
      this.searchMethod();
    }
  },
  methods: {
    vehicleTextFilter,
    getRepositories() {
      getRequest(
        "/repository/",
        `role=${this.$store.getters.role}&institution_id=${
          this.$store.getters.institutionId
        }`,
      )
        .then((response) => {
          this.repositories = response.repositories;
        })
        .catch(() => {});
    },
    getLogs(userId) {
      getRequest(`/plate-logs/${userId}`)
        .then((res) => {
          if (res && res.logs) {
            this.logs = res.logs;
            this.logsCount = res.log_count || 0;

            // ✅ prepare autocomplete suggestions here
            const sorted = [...this.logs].sort(
              (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
            );

            this.lastFivePlates = [
              ...new Set(sorted.map((l) => l.plate)),
            ].slice(0, 5);
          }
        })
        .catch((error) => {});
    },
    formatDateToDisplay(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0"); // 0-indexed
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
    onGroupSelected(group) {
      if (group) {
        this.titleCodes = group.codes || [];
        this.selectingItemCodes = true;

        this.$nextTick(() => {
          setTimeout(() => {
            this.menuMultipleOpen = false;
          }, 100);
          this.searchMethod();
        });
      } else {
        this.titleCodes = [];
      }
    },
    onGroupNoteSelected(group) {
      if (group) {
        this.titleCodes = group.codes || [];
        this.selectingItemCodes = true;

        this.$nextTick(() => {
          setTimeout(() => {
            this.menuMultipleOpen = false;
          }, 100);
        });
      } else {
        this.titleCodes = [];
      }
    },
    onGroupOtherSelected(group) {
      if (group) {
        this.titleCodes = group.codes || [];
        this.selectingItemCodes = true;

        this.$nextTick(() => {
          setTimeout(() => {
            this.menuMultipleOpen = false;
          }, 100);
        });
      } else {
        this.titleCodes = [];
      }
    },
    getLocale() {
      this.searchTypes[0].label = this.$t("parts.inventory.title");
      this.searchTypes[1].label = `${this.$t("parts.inventory.title")}+${this.$t("parts.table.manufacturer_code")}`;
      this.searchTypes[2].label = this.$t("parts.inventory.other");
      this.searchTypes[3].label = this.$t("parts.inventory.multiple_titles");
      this.searchTypes[4].label = `${this.$t("parts.inventory.multiple_titles")}+${this.$t("parts.inventory.form.notes")}`;
      this.searchTypes[5].label = `${this.$t("parts.inventory.multiple_titles")}+${this.$t("parts.inventory.other")}`;
      this.searchTypes[6].label = this.$t("parts.inventory.id");
      this.searchTypes[7].label = this.$t("parts.inventory.pra");
      this.searchTypes[8].label = this.$t("parts.inventory.engine_code");
      this.statusItems[0].title = this.$t("parts.status.entered");
      this.statusItems[1].title = this.$t("parts.status.returned");
      this.statusItems[2].title = this.$t("parts.status.returned_shop");
      this.statusItems[3].title = this.$t("parts.status.re_entered");
      this.statusItems[4].title = this.$t("parts.status.found");
      this.supplies[0].title = this.$t("parts.advanced_search.supply.petrol");
      this.supplies[1].title = this.$t("parts.advanced_search.supply.diesel");
      this.supplies[2].title = this.$t("parts.advanced_search.supply.gas");
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
    setYearRule(startingDate, endingDate) {
      const startingYear = startingDate
        ? new Date(startingDate).getFullYear()
        : 1900;
      const endingYear = endingDate
        ? new Date(endingDate).getFullYear()
        : new Date().getFullYear();
      this.yearRule = (v) =>
        (v >= startingYear && v <= endingYear) ||
        this.$t("parts.advanced_search.year_error");
    },
    generatePricesList() {
      this.prices = [];
      for (let i = 50; i <= 5000; i += 50) {
        this.prices.push(i);
      }
    },
    searchMethod() {
      if (this.advancedSearch) {
        if (this.advancedSearchItems.startDate) {
          this.advancedSearchItems.startDate = this.formatDateToDisplay(
            this.advancedSearchItems.startDate,
          );
        }
        if (this.advancedSearchItems.endDate) {
          this.advancedSearchItems.endDate = this.formatDateToDisplay(
            this.advancedSearchItems.endDate,
          );
        }
        if (!this.advancedSearchItems.positionCode) {
          this.advancedSearchItems.positionCode = "";
        }
        if (!this.advancedSearchItems.position) {
          this.advancedSearchItems.position = "";
        }
        // ADVANCED SEARCH
        this.$emit("adv-search", {
          type: this.searchType,
          searchField: this.inventorySearchField,
          titleCode: this.titleCode,
          titleCodes: this.titleCodes,
          advancedSearchFields: this.advancedSearchItems,
          note: this.note,
          code: this.code,
        });
      } else if (
        (this.searchType === "multipleTitlesOther"
          && this.groupGeneralSearch.length > 0
          && this.titleCodes
          && this.titleCodes.length)
        || (this.searchType !== "multipleTitlesOther"
          && ((this.search.length > 0
            || this.titleCode
            || (this.titleCodes && this.titleCodes.length)
            || (this.code && this.code.trim())
            || (this.note && this.note.trim()))
            || (this.searchType === "engineCode" && this.search.trim().length > 0)))
      ) {
        // REGULAR SEARCH
        this.$emit("search", {
          type: this.searchType,
          searchField: this.inventorySearchField,
          titleCode: this.titleCode,
          titleCodes: this.titleCodes,
          note: this.note,
          code: this.code,
        });
      }
      this.menuOpen = false;
      this.menuMultipleOpen = false;
      setTimeout(() => {
        this.$router.replace({
          path: this.$route.path,
          query: {},
        });
      }, 5000);
    },
    onQualityRatingSelect(val) {
      if (this.selectedQualityRating === val || val === 0) {
        // toggle off → reset to all qualities
        this.selectedQualityRating = 0;
        this.advancedSearchItems.quality = this.qualityItems.map((q) => q._id);
      } else {
        this.selectedQualityRating = val;
        const matches = this.qualityItems
          .filter((q) => q.rating === val)
          .map((q) => q._id);
        this.advancedSearchItems.quality = matches.length
          ? matches
          : this.qualityItems.map((q) => q._id);
      }
    },
    clearSearchFields() {
      this.search = "";
      this.groupGeneralSearch = "";
      this.titleCode = null;
      this.titleCodes = null;
      this.note = "";
      this.code = "";

      this.advancedSearchItems = {
        brand: null,
        model: null,
        version: null,
        institution: null,
        shipment: null,
        status: [1, 2, 2.1, 3, 4],
        quality: [],
        price: [0, 5000],
        startDate: "",
        endDate: "",
        noApplicability: false,
        noNotes: false,
        noTitleNoOem: false,
        customApplicability: false,
        labelOrigin: "",
        positionMismatch: false,
        eBayStatus: "",
        no_shipping_cost: true,
        invalid_category: true,
        isNewPart: "",
        positionCode: "",
        position: "",
        repository: null,
        room: null,
        location: null,
      };
      this.year = null;
      this.supply = null;
      this.plate = null;
      this.brand = null;
      this.model = null;
      this.version = null;
      this.engine = null;
      this.models = [];
      this.versions = [];
      this.qualityItems.forEach((quality) =>
        this.advancedSearchItems.quality.push(quality._id),
      );
      this.selectedQualityRating = 0;
      this.$emit("clear-search");
    },

    getBrands() {
      this.loadingBrand = true;
      getRequest("/info-car/brands")
        .then((response) => {
          this.brands = response.brands;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBrand = false;
        });
    },
    getModels(brandCode) {
      this.loadingModel = true;
      getRequest("/info-car/models", `brand_code=${brandCode}`)
        .then((response) => {
          this.models = response.models;
          this.filterModels();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingModel = false;
        });
    },
    getVersions(brandCode, modelCode) {
      this.loadingVersion = true;
      getRequest(
        "/info-car/versions",
        `brand_code=${brandCode}&model_code=${modelCode}`,
      )
        .then((response) => {
          this.versions = response.versions;
          this.filterVersions();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingVersion = false;
        });
    },

    filterModels() {
      if (
        this.year &&
        this.year.length === 4 &&
        this.models &&
        this.models.length > 0
      ) {
        this.models = this.models.filter((model) => {
          const sYear = model.startingDate
            ? new Date(model.startingDate).getFullYear()
            : 1900;
          const eYear = model.endingDate
            ? new Date(model.endingDate).getFullYear()
            : new Date().getFullYear();

          return this.year >= sYear && this.year <= eYear;
        });
      }
    },
    filterVersions() {
      if (
        this.brand &&
        this.brand.brandCode &&
        this.model &&
        this.model.modelCode &&
        this.versions &&
        this.versions.length > 0
      ) {
        if (this.year) {
          // filter by year
          this.versions = this.versions.filter((version) => {
            const sYear = version.startingDate
              ? new Date(version.startingDate).getFullYear()
              : 1900;
            const eYear = version.endingDate
              ? new Date(version.endingDate).getFullYear()
              : new Date().getFullYear();

            return this.year >= sYear && this.year <= eYear;
          });
        }

        if (this.supply) {
          // filter by supply
          this.versions = this.versions.filter(
            (version) => version.supplyCode === this.supply,
          );
        }
      }
    },

    scanQr() {
      this.$emit("scan-qr");
    },
    exportData() {
      // Transform items for export
      return this.$props.items.map((item) => ({
        Status:
          item.status === 1
            ? "Entered"
            : item.status === 2
              ? "Returned-Sales"
              : item.status === 2.1
                ? "Returned-Shop"
                : item.status === 3
                  ? "Re-Entered"
                  : item.status === 4
                    ? "Found"
                    : item.status === 5
                      ? "Sold"
                      : item.status === 6
                        ? "Lost"
                        : item.status === 7
                          ? "Damaged"
                          : item.status === 8
                            ? "Damaged-Sales"
                            : item.status === 8.1
                              ? "Damaged-Shop"
                              : item.status === 9
                                ? "Damaged-Lost"
                                : item.status === 0
                                  ? "Recycled"
                                  : "",
        ID: item.old_id || item._id,
        Title:
          item.infoCar?.description?.descriptionId?.title?.[
            this.$store.getters.language
          ] || "",
        "Manufacturer Code": item.manufacturer_code,
        Notes: item.notes || "",
        Quality: item.quality?.title || "",
        Location:
          item.shelf_id && item.shelf_data
            ? `${item.shelf_data.shelf.room_name}-${item.shelf_data.shelf.location_code}: ${item.shelf_data.shelf.x}, ${item.shelf_data.shelf.y}`
            : "",
        Price: `${item.price} ${item.currency}`,
        "Date Created": moment(item.date_created).format("DD-MM-YYYY LT"),
      }));
    },
    async exportToExcel() {
      const data = await this.exportData();

      // Create a worksheet
      const ws = XLSX.utils.json_to_sheet(data);

      // Create a workbook and append the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inventory");

      // Trigger download
      XLSX.writeFile(wb, "inventory.xlsx");
    },
    async exportDataEbay() {
      const filteredItems = this.$props.items.filter(
        (item) => item.eBayStatus === false,
      );
      const itemIds = filteredItems.map((item) => item._id);

      try {
        const response = await axios.post(
          `${this.baseUrl}/ebay/prepare-items-excel`,
          { ids: itemIds, institutionId: this.$store.getters.institutionId },
          { headers: { authorization: localStorage.token } },
        );

        // Handle different response shapes safely
        const excelData = Array.isArray(response.data)
          ? response.data
          : response.data.items || response.data.data || [];

        if (!Array.isArray(excelData) || excelData.length === 0) {
          return;
        }

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Ebay Items");

        XLSX.writeFile(wb, "ebay_items.xlsx");
      } catch {}
    },
    syncEbay() {
      localStorage.setItem("synchronizeEbay", true);
      this.$emit("sync-with-ebay");
    },
    exportArchive() {
      this.$emit("export-archive");
    },
    share() {
      this.$emit("share");
    },
    deleteParts() {
      this.$emit("delete-parts");
    },
    addEbayItems() {
      this.$emit("add-multiple-ebay", this.$props.items);
    },
    deleteEbayItems() {
      this.$emit("delete-multiple-ebay", this.$props.items);
    },
    addGoogleItems() {
      this.$emit("add-multiple-google", this.$props.items);
    },
    deleteGoogleItems() {
      this.$emit("delete-multiple-google", this.$props.items);
    },
    checkEbayStatus(items) {
      return items.every((item) => item.eBayStatus === true);
    },
    checkGoogleStatus(items) {
      return items.every((item) => item.googleMerchantStatus === true);
    },
    exportPdf() {
      if (this.advancedSearch) {
        this.$emit("export-pdf-advanced", {
          type: this.searchType,
          searchField: this.inventorySearchField,
          titleCode: this.titleCode,
          advancedSearchFields: this.advancedSearchItems,
        });
      } else {
        this.$emit("export-pdf", {
          type: this.searchType,
          searchField: this.inventorySearchField,
          titleCode: this.titleCode,
        });
      }
    },
    startLoading(type) {
      if (type === "archive") this.loadingArchive = true;
      if (type === "pdf") this.loadingPdf = true;
      if (type === "delete-part") this.loadingDeletePart = true;
    },
    stopLoading(type) {
      if (type === "archive") this.loadingArchive = false;
      if (type === "pdf") this.loadingPdf = false;
      if (type === "delete-part") this.loadingDeletePart = false;
    },
    getQualities() {
      this.loadingQuality = true;
      getRequest("/quality/")
        .then((response) => {
          this.qualityItems = response.qualities;
          this.advancedSearchItems.quality = [];
          this.qualityItems.forEach((quality) =>
            this.advancedSearchItems.quality.push(quality._id),
          );
          this.loadingQuality = false;
        })
        .catch(() => {
          this.loadingQuality = false;
        });
    },
    getInstitutions() {
      this.loadingInstitution = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions;
          this.loadingInstitution = false;
        })
        .catch(() => {
          this.loadingInstitution = false;
        });
    },
    getInstitution() {
      getRequest("/institution", `id=${this.$store.getters.institutionId}`)
        .then((response) => {
          if (response.institution && response.institution.length > 0) {
            this.canSearchPlates = !!(
              response.institution[0].plates.remaining > 0
            );
          }
        })
        .catch(() => {});
    },
    getShipments(institutionId) {
      this.loadingShipment = true;
      getRequest("/shipment/institution", `id=${institutionId}`)
        .then((response) => {
          this.shipments = response.shipments;
          this.loadingShipment = false;
        })
        .catch(() => {
          this.loadingShipment = false;
        });
    },
    formatPlate(value) {
      if (value && value.target) value = value.target.value;

      const input = String(value)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
      let result = "";

      for (let i = 0; i < input.length; i++) {
        const char = input[i];

        // First 2 → letters
        if (result.length < 2) {
          if (/[A-Z]/.test(char)) result += char;
          continue;
        }

        // Next 3 → numbers
        if (result.length >= 2 && result.length < 5) {
          if (/[0-9]/.test(char)) result += char;
          continue;
        }

        // Last 2 → letters
        if (result.length >= 5 && result.length < 7) {
          if (/[A-Z]/.test(char)) result += char;
        }

        if (result.length === 7) break;
      }

      this.plate = result;
    },
    // formatPlate(value) {
    //   if (value && value.target) {
    //     value = value.target.value;
    //   }
    //   if (!value) {
    //     this.plate = "";
    //     return;
    //   }

    //   // Convert to uppercase and clean to alphanumeric
    //   const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    //   let result = "";

    //   // 1) First 2 chars must be letters
    //   const letters = cleaned.match(/[A-Z]/g) || [];
    //   result += letters.slice(0, 2).join("");

    //   // 2) 3rd char must not be a letter (restrict to numbers or other non-letters)
    //   const thirdChar = cleaned.match(/[0-9]/g) || [];
    //   result += thirdChar.slice(0, 1).join("");

    //   // 3) Next 3 chars must be numbers (limit to 3)
    //   const numbers = cleaned.match(/[0-9]/g) || [];
    //   const startIndex = result.length > 2 ? 1 : 0; // Skip the 3rd char if already used
    //   result += numbers.slice(startIndex, startIndex + 3).join("");

    //   // 4) Last 2 chars must be letters (limit to 2)
    //   const remainingLetters = letters.slice(2, 4).join("");
    //   result += remainingLetters;

    //   // 5) Enforce maximum 7 chars total
    //   this.plate = result.slice(0, 7);
    // },
    searchPlate() {
      if (this.plate && this.plate.length > 0) {
        this.loadingPlate = true;
        getRequest(`/info-car/plate/${this.plate}`, "")
          .then(async (response) => {
            try {
              const { brands } = await getRequest("/info-car/brands");
              this.brands = brands;
              this.brand = await getRequest(
                "/info-car/brand",
                `id=${response.brandId}`,
              );

              const { models } = await getRequest(
                "/info-car/models",
                `brand_code=${response.brandCode}`,
              );
              this.models = models;
              this.model = await getRequest(
                "/info-car/model",
                `id=${response.modelId}`,
              );

              const { versions } = await getRequest(
                "/info-car/versions",
                `brand_code=${response.brandCode}&model_code=${response.modelCode}`,
              );
              this.versions = versions;
              this.version = await getRequest(
                "/info-car/version",
                `id=${response.versionId}`,
              );

              this.foundPlate = true;
              let data = {
                user_id: this.$store.getters.userId,
                plate: this.plate.toUpperCase(),
              };
              postRequest
                .object(`/plate-logs`, data)
                .then(() => {
                  // console.log('res',res)
                })
                .catch(() => {});
              this.getInstitution();
            } catch (error) {
              this.$emit("alert", {
                message: this.$t("parts.advanced_search.plate_not_found"),
                type: "error",
                timeout: 5000,
              });
              this.foundPlate = false;
            }
          })
          .catch((error) => {
            this.$emit("alert", {
              message: this.$t("parts.advanced_search.plate_not_found"),
              type: "error",
              timeout: 5000,
            });
            this.foundPlate = false;
          })
          .finally(() => {
            this.loadingPlate = false;
          });
      }
    },
    confirmSyncPrice() {
      this.confirmDialog = true;
    },
    confirmSyncRrShipping() {
      this.confirmSyncRrDialog = true;
    },
    syncRrShipping() {
      this.loadingSyncRrShipping = true;
      postRequest
        .object("/part/inventory/sync-rr-shipping-costs", {})
        .then((response) => {
          const modified =
            response && response.data && response.data.modified
              ? response.data.modified
              : 0;
          this.$emit("alert", {
            message:
              modified > 0
                ? this.$t("parts.inventory.sync_rr_shipping_success", {
                    count: modified,
                  })
                : this.$t("parts.inventory.sync_rr_shipping_none"),
            type: "success",
            timeout: 5000,
          });
          this.$emit("synced");
        })
        .catch(() => {
          this.$emit("alert", {
            message: this.$t("parts.inventory.sync_rr_shipping_fail"),
            type: "error",
            timeout: 5000,
          });
        })
        .finally(() => {
          this.loadingSyncRrShipping = false;
          this.confirmSyncRrDialog = false;
        });
    },
    syncPrice() {
      this.loadingSyncPrice = true;
      getRequest(
        "/part/sync-price",
        `institution_id=${this.$store.getters.institutionId}`,
      )
        .then((response) => {
          this.$emit("synced");
          this.confirmDialog = false;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingSyncPrice = false;
          this.confirmDialog = false;
        });
    },
    openInfoCarCad() {
      this.$emit("infocar-cad", {
        brandCode: this.brand.brandCode,
        modelCode: this.model.modelCode,
        versionCode: this.version.versionCode,
      });
    },
    openCarPartsList() {
      this.$emit("car-parts-list", {
        car: { brand: this.brand, model: this.model, version: this.version },
      });
    },
    openSearchList() {
      this.$emit("search-list");
    },
    setTitle(data) {
      // Triggered by the car parts list and searchList
      // Force to search by title.
      this.searchType = "title";
      // Set title.
      this.loadingTitle = true;
      getRequest("/info-car/part", `title_code=${data.code}`)
        .then((response) => {
          this.titles = [response.part];
          this.titleCode = response.part.partCode;
          // Search
          this.searchMethod();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingTitle = false;
        });
    },
    setGroup(data) {
      // Triggered by the searchList
      // Force search by multipleTitles
      this.searchType = "multipleTitles";
      // Set Multiple titles
      this.loadingMultipleTitles = true;
      getRequest("/info-car/partgroup", `group_id=${data.groupId}`)
        .then((response) => {
          this.titleGroups = [response.partgroup];
          this.titleCodes = response.partgroup.codes;
          // Search
          this.searchMethod();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingMultipleTitles = false;
        });
    },
    highlight(title, search) {
      let result = title || "";
      if (search) {
        const index = title
          .toLowerCase()
          .indexOf(search.toLocaleLowerCase());
        // mark the text.
        if (index > -1)
          result = [
            title.slice(0, index),
            "<mark>",
            title.slice(index, index + search.length),
            "</mark>",
            title.slice(index + search.length),
          ].join("");
      }
      return `<span>${result}</span>`;
    },
    setLabelOrigin(origin, checked) {
      this.advancedSearchItems.labelOrigin = checked ? origin : "";
    },
    toggleNoApplicabilityDropdown() {
      this.noApplicabilityDropdown = !this.noApplicabilityDropdown;
      if (this.noApplicabilityDropdown) {
        this.advancedSearchItems.noApplicability = true;
        this.advancedSearchItems.noNotes = true;
        this.advancedSearchItems.customApplicability = false;
        this.advancedSearchItems.labelOrigin = "";
      } else {
        this.advancedSearchItems.noApplicability = false;
        this.advancedSearchItems.noNotes = false;
        this.advancedSearchItems.customApplicability = false;
        this.advancedSearchItems.labelOrigin = "";
      }
    },
    toggleNoTitleNoOemFilter() {
      this.advancedSearchItems.noTitleNoOem =
        !this.advancedSearchItems.noTitleNoOem;
    },
    togglePositionMismatchFilter() {
      this.advancedSearchItems.positionMismatch =
        !this.advancedSearchItems.positionMismatch;
    },
    setCarDetails(title, brand, model, version) {
      return new Promise(async (resolve, reject) => {
        try {
          // title
          this.loadingTitle = true;
          const { part } = await getRequest(
            "/info-car/part",
            `title_code=${title}`,
          );
          this.titles = [part];
          this.titleCode = part.partCode;
          this.loadingTitle = false;
          // brand
          this.loadingBrand = true;
          const { brands } = await getRequest("/info-car/brands");
          this.brands = brands;
          this.brand = this.brands.find((i) => i.brandCode === brand);
          this.loadingBrand = false;
          // model
          this.loadingModel = true;
          const { models } = await getRequest(
            "/info-car/models",
            `brand_code=${this.brand.brandCode}`,
          );
          this.models = models;
          this.model = this.models.find((i) => i.modelCode === model);
          this.loadingModel = false;
          // version
          this.loadingVersion = true;
          const { versions } = await getRequest(
            "/info-car/versions",
            `brand_code=${this.brand.brandCode}&model_code=${this.model.modelCode}`,
          );
          this.versions = versions;
          this.version = this.versions.find((i) => i.versionCode === version);
          this.loadingVersion = false;
          //
          resolve();
        } catch (error) {
          this.loadingTitle = false;
          this.loadingBrand = false;
          this.loadingModel = false;
          this.loadingVersion = false;
          reject(error);
        }
      });
    },
    setId(id) {
      this.searchType = "id";
      this.$nextTick(() => {
        this.search = id;
      });
    },
    searchByOem(oem) {
      const code = String(oem || "").trim();
      if (!code) return;
      this.advancedSearch = false;
      const alreadyOther = this.searchType === "other";
      this.searchType = "other";
      this.titleCode = null;
      this.titleCodes = [];
      this.code = "";
      this.note = "";
      const apply = () => {
        this.search = code;
        this.$nextTick(() => this.searchMethod());
      };
      if (alreadyOther) apply();
      else this.$nextTick(apply);
    },
    editMultiple() {
      this.$emit("open-multiple-edit", this.$props.items);
    },
    sellMultiple() {
      if (this.$props.items && this.$props.items.length > 20) {
        this.errorMsg = this.$t("max_20_parts");
        this.errorModal = true;
        return;
      }
      this.$emit("open-sell-multiple", this.$props.items);
    },
    closeErrorModal() {
      this.errorMsg = "";
      this.errorModal = false;
    },
    customFilter(item, queryText, itemText) {
      if (!queryText) return true;
      const searchText = queryText.toLowerCase();
      const language = this.$store.getters.language;

      // Check main title
      const mainTitle = itemText.raw?.title?.[language]?.toLowerCase() || "";
      if (mainTitle.includes(searchText)) return true;

      // Check related titles
      const relatedTitles = itemText.raw?.related || [];
      return relatedTitles.some((related) => {
        const relatedTitle = related?.title?.[language]?.toLowerCase() || "";
        return relatedTitle.includes(searchText);
      });
    },
    updateSearch(value) {
      this.searchTitle = value;
    },
    selectCode(item) {
      this.titleCode = item.raw?.partCode || item.partCode || item.value;
      this.selectingItem = true;
      if (this.searchType !== "titleAndCode") {
        this.$nextTick(() => {
          this.searchMethod();
        });
      }
    },
    selectPlate(p) {
      this.plate = p;
      this.showDropdown = false;
      this.searchPlate();
    },
    onBlur() {
      // Delay hiding dropdown to allow click on item
      setTimeout(() => {
        this.showDropdown = false;
      }, 100);
    },
  },
};
</script>
<style scoped>
.related-scroll {
  overflow-x: auto;
  white-space: nowrap;
  margin-top: -8px;
  max-width: 400px;
}

.related-scroll span {
  white-space: nowrap;
}

/* Search row fills the red bar at every width. Fields stay full width until there is room to sit beside the type select. */
.inventory-toolbar-strip {
  position: relative;
  z-index: 1;
  width: 100%;
}

.inventory-toolbar-root .min-width-0 {
  min-width: 0;
}

.inventory-toolbar-root .inventory-toolbar-right {
  flex: 1 1 100%;
  max-width: 100%;
}

.inventory-toolbar-root .inventory-toolbar-fields-row,
.inventory-toolbar-root .inventory-toolbar-type-row {
  flex: 1 1 100%;
  width: 100%;
  max-width: 100%;
}

.inventory-toolbar-root .inventory-toolbar-fields,
.inventory-toolbar-root .inventory-toolbar-fields > *,
.inventory-toolbar-root .inventory-toolbar-double-field,
.inventory-toolbar-root .inventory-toolbar-double-field > * {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.inventory-toolbar-root :deep(.inventory-field-on-red) {
  width: 100%;
}

.inventory-toolbar-root :deep(.inventory-field-on-red .v-field) {
  width: 100%;
  min-width: 0;
}

.inventory-toolbar-root .inventory-toolbar-type-select {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  max-width: 100%;
}

.inventory-toolbar-root :deep(.inventory-toolbar-type-select .v-field__input) {
  flex-wrap: nowrap;
}

.inventory-toolbar-root :deep(.inventory-toolbar-type-select .v-select__selection-text) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 599.98px) {
  .inventory-toolbar-root .inventory-toolbar-type-row {
    flex-wrap: wrap;
  }

  .inventory-toolbar-root .inventory-toolbar-type-select {
    flex: 1 1 100%;
  }
}

@media (min-width: 960px) {
  .inventory-toolbar-root .inventory-toolbar-search-row {
    flex-wrap: nowrap;
    align-items: center;
  }

  .inventory-toolbar-root .inventory-toolbar-right {
    flex: 1 1 0%;
    width: auto;
    max-width: 100%;
  }

  .inventory-toolbar-root .inventory-toolbar-brand {
    max-width: min(38vw, 26rem);
  }
}

@media (min-width: 1280px) {
  .inventory-toolbar-root .inventory-toolbar-fields-row {
    flex: 1 1 auto;
    width: auto;
    max-width: calc(100% - 22rem);
  }

  .inventory-toolbar-root .inventory-toolbar-type-row {
    flex: 0 0 auto;
    width: auto;
    max-width: none;
  }

  .inventory-toolbar-root .inventory-toolbar-type-select,
  .inventory-toolbar-root .inventory-toolbar-type-select--combo {
    flex: 0 0 18rem;
    width: 18rem;
  }

  .inventory-toolbar-root .inventory-toolbar-double-field > * {
    width: auto;
    flex: 1 1 0;
    min-width: 11rem;
  }
}

.inventory-toolbar-actions-btns .inventory-toolbar-action-btn {
  flex: 1 1 11.5rem;
}

@media (min-width: 1280px) {
  .inventory-toolbar-actions-btns .inventory-toolbar-action-btn {
    flex: 0 1 auto;
  }
}

.inventory-toolbar-scan-btn {
  width: min(100%, 22rem);
}

@media (min-width: 1280px) {
  .inventory-toolbar-scan-btn {
    width: auto;
  }
}

/* Model/version combobox: wrap details on mobile — taller field, no horizontal scroll */
@media (max-width: 959px) {
  .inventory-toolbar-root .inventory-version-detail-selection {
    width: 100%;
    padding: 0.375rem 0.25rem;
  }

  .inventory-toolbar-root .inventory-version-detail-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    width: 100%;
    gap: 0.75rem 1rem;
  }

  .inventory-toolbar-root .inventory-version-detail-col {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
    flex: 1 1 0;
  }

  .inventory-toolbar-root .inventory-version-detail-col .caption {
    line-height: 1.25;
    margin-bottom: 0.125rem;
  }

  .inventory-toolbar-root .inventory-version-detail-col .font-weight-bold {
    line-height: 1.4;
    word-break: break-word;
  }

  .inventory-toolbar-root .inventory-car-combobox :deep(.v-field) {
    --v-field-padding-top: 10px;
    --v-field-padding-bottom: 10px;
    height: auto !important;
    min-height: 3.5rem;
  }

  .inventory-toolbar-root .inventory-version-combobox :deep(.v-field) {
    --v-field-padding-top: 12px;
    --v-field-padding-bottom: 12px;
    min-height: 4.5rem;
  }

  .inventory-toolbar-root .inventory-car-combobox :deep(.v-field__field) {
    height: auto !important;
    min-height: 3.5rem;
    align-items: flex-start;
  }

  .inventory-toolbar-root .inventory-version-combobox :deep(.v-field__field) {
    min-height: 4.5rem;
  }

  .inventory-toolbar-root .inventory-car-combobox :deep(.v-field__input) {
    white-space: normal;
    overflow: visible;
    align-items: flex-start;
    flex-wrap: wrap;
    min-height: unset;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .inventory-toolbar-root .inventory-version-combobox :deep(.v-field__input) {
    display: block !important;
    height: auto !important;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .inventory-toolbar-root .inventory-car-combobox :deep(.v-combobox__selection) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    width: 100%;
    max-width: 100%;
  }

  .inventory-toolbar-root .inventory-version-combobox :deep(.v-combobox__selection) {
    display: block !important;
    width: 100%;
    position: static !important;
    transform: none !important;
  }

  .inventory-toolbar-root .inventory-car-detail-selection {
    overflow: visible;
    width: 100%;
  }


  .inventory-toolbar-root .inventory-version-detail-row,
  .inventory-toolbar-root .inventory-version-detail-row--dropdown {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem 0.875rem;
    flex-wrap: unset;
  }

  .inventory-toolbar-root .inventory-version-detail-col {
    flex: unset;
    padding: 0;
  }

  .inventory-toolbar-root .inventory-version-detail-col--main {
    grid-column: 1 / -1;
    padding-bottom: 0.625rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .inventory-toolbar-root .inventory-version-detail-col--main .font-weight-bold {
    line-height: 1.45;
  }

  .inventory-toolbar-root .inventory-car-detail-col {
    flex: 1 1 calc(50% - 0.5rem);
    max-width: none;
    min-width: 0;
    padding: 0;
    word-break: break-word;
  }

  .inventory-toolbar-root .inventory-car-detail-selection.d-flex {
    flex-wrap: wrap;
    gap: 0.625rem 0.75rem;
  }
}

/* Advanced search: mobile-friendly grid (md+ keeps original column proportions) */
.inventory-advanced-search-panel {
  overflow-x: hidden;
}

@media (min-width: 600px) {
  .inventory-advanced-search-panel {
    overflow-y: hidden;
  }
}

.inventory-toolbar-root .inventory-advanced-divider-row {
  flex-wrap: wrap;
  justify-content: center;
  row-gap: 0.25rem;
}

.inventory-toolbar-root .inventory-advanced-divider-label {
  white-space: normal;
  line-height: 1.3;
  width: 100%;
  max-width: 100%;
}

@media (min-width: 600px) {
  .inventory-toolbar-root .inventory-advanced-divider-row {
    flex-wrap: nowrap;
  }

  .inventory-toolbar-root .inventory-advanced-divider-label {
    white-space: nowrap;
    width: auto;
  }
}
</style>
